import { call, takeLatest, all } from 'redux-saga/effects';
import * as api from '@api/validationSpecialistSubjects';
import { actions } from './ValidationSpecialistSubjects.slice';
import {
  ValidationSpecialistSubjectsListFetchAction,
  ValidationSpecialistSubjectDetailsFetchAction,
  ValidationSpecialistVisitUploadsFetchAction,
  ValidationSpecialistContextFetchAction,
  ClinicalTrialContext,
  ValidationSpecialistUpload,
} from './ValidationSpecialistSubjects.types';
import { Resp, dispatch } from '../helpers';
import { runToast } from '../tools';
import { SiteSubjectDetails } from '@dynamika-shared/types/subjects';

export function* fetchValidationSpecialistSubjectsListWorker(
  action: ValidationSpecialistSubjectsListFetchAction
) {
  try {
    const { clinicalTrialId, args } = action.payload;
    const response: Resp<typeof api.fetchValidationSpecialistSubjects> = yield call<
      typeof api.fetchValidationSpecialistSubjects
    >(api.fetchValidationSpecialistSubjects, clinicalTrialId, args);
    yield dispatch(actions.fetchValidationSpecialistSubjectsListSuccess(response));
  } catch (error) {
    yield dispatch(actions.fetchValidationSpecialistSubjectsListFailure(error as Error));
  }
}

export function* fetchValidationSpecialistSubjectDetailsWorker(
  action: ValidationSpecialistSubjectDetailsFetchAction
) {
  const { clinicalTrialId, patientId } = action.payload;
  try {
    const [patientDetails, visitsResponse]: [
      Resp<typeof api.fetchValidationSpecialistSubjectDetails>,
      Resp<typeof api.fetchValidationSpecialistPatientVisits>
    ] = yield all([
      call(api.fetchValidationSpecialistSubjectDetails, clinicalTrialId, patientId),
      call(api.fetchValidationSpecialistPatientVisits, clinicalTrialId, patientId),
    ]);
    
    const combinedResponse = {
      id: patientDetails.id,
      dob: patientDetails.dob,
      cohortId: patientDetails.cohortName,
      patientCode: patientDetails.code,
      siteName: patientDetails.siteName,
      status: patientDetails.status,
      visits: visitsResponse.visits.map(({ visit, numberUploadsToValidate }) => ({
        id: visit.id,
        numberUploadsToValidate,
        visitName: visit.visitName,
        visitDefinitionId: visit.visitDefinitionId,
        status: visit.visitStatus,
        expectedDate: visit.forecastDate,
        latestUpload: visit.uploadDate,
        type: '',
        assetAcquisitionDate: visit.assetAcquisitionDate,
        visitOrder: visit.visitOrder,
        visitDate: visit.visitDate,
        earlyTerminated: false,
      })),
    };

    yield dispatch(actions.fetchValidationSpecialistSubjectDetailsSuccess(combinedResponse as Partial<SiteSubjectDetails> | undefined));
  } catch (error) {
    yield dispatch(actions.fetchValidationSpecialistSubjectDetailsFailure(error as Error));
  }
}

export function* fetchValidationSpecialistVisitUploadsWorker(action: ValidationSpecialistVisitUploadsFetchAction): Generator {
  try {
    const { clinicalTrialId, visitId } = action.payload;
    const response = (yield call(
      api.fetchValidationSpecialistVisitUploads,
      clinicalTrialId,
      visitId
    ));

    // filter out non HRPQCT uploads for Validation Specialist
    const validationSpecialistUploads = response.uploads.filter(
      (upload: ValidationSpecialistUpload) => upload.dataType === 'HRPQCT');
    
    yield dispatch(actions.fetchValidationSpecialistVisitUploadsSuccess({ uploads: validationSpecialistUploads }));
  } catch (error) {
    yield dispatch(actions.fetchValidationSpecialistVisitUploadsFailure(error as Error));
  }
}


export function* fetchContextWorker(action: ValidationSpecialistContextFetchAction): Generator {
  try {
    const { clinicalTrialId } = action.payload;
    const response = (yield call(
      api.fetchClinicalTrialContext,
      clinicalTrialId
    )) as ClinicalTrialContext;
    yield dispatch(actions.fetchContextSuccess(response));
  } catch (error) {
    yield dispatch(actions.fetchContextFailure(error as Error));
    yield call(runToast, 'error', 'Failed to fetch clinical trial context');
  }
}

export function* ValidationSpecialistSubjectsSaga() {
  yield takeLatest(
    actions.fetchValidationSpecialistSubjectsList,
    fetchValidationSpecialistSubjectsListWorker
  );
  yield takeLatest(
    actions.fetchValidationSpecialistSubjectDetails,
    fetchValidationSpecialistSubjectDetailsWorker
  );

  yield takeLatest(
    actions.fetchValidationSpecialistVisitUploads,
    fetchValidationSpecialistVisitUploadsWorker
  );

  yield takeLatest(actions.fetchContext, fetchContextWorker);
}
